/**
 * The investigator's envelope, read once into the shape the report draws. It is schemaless
 * in transit, so a wrong type reads as absent and that part of the report does not draw.
 */

import { investigationVerdict, num, oneOf, rec, records, str, strings } from './verdict-readers';
import { RUNG_LABELS } from './rungs';

type RungStatus = 'ok' | 'broken' | 'not_evaluable';
const RUNG_STATUSES: readonly RungStatus[] = ['ok', 'broken', 'not_evaluable'];

export interface LadderRung {
  key: string;
  label: string;
  status: RungStatus;
  note: string | null;
}

export interface FixSuggestion {
  text: string;
  targetType: string | null;
}

export interface EvidenceItem {
  /** A span id or the context tool the observation came from; either, never both. */
  ref: string | null;
  observation: string;
}

export interface DecisionItem {
  decision: string;
  reasoning: string | null;
}

export interface DeliveryBlock {
  status: string;
  note: string | null;
}

export interface AdditionalDefect {
  rung: string | null;
  fixKey: string | null;
  summary: string | null;
  expected: string | null;
  actual: string | null;
  fixes: FixSuggestion[];
  confidence: number | null;
}

export interface VerdictReport {
  disposition: string | null;
  severity: string | null;
  fixKey: string | null;
  rootCause: string | null;
  expected: string | null;
  actual: string | null;
  confidence: number | null;
  ladder: LadderRung[];
  firstBrokenRung: string | null;
  delivery: DeliveryBlock | null;
  causeChain: string[];
  evidence: EvidenceItem[];
  decisions: DecisionItem[];
  fixes: FixSuggestion[];
  additionalDefects: AdditionalDefect[];
  meta: {
    model: string | null;
    iterations: number | null;
    wallSeconds: number | null;
    costUsd: number | null;
    traceUrl: string | null;
  };
}

const readFixes = (value: unknown): FixSuggestion[] =>
  records(value)
    .map((fix) => ({ text: str(fix['text']), targetType: str(fix['target_type']) }))
    .filter((fix): fix is FixSuggestion => fix.text !== null);

/** The ladder in climbing order; a rung the investigator did not name, or never reached, is left out. */
const readLadder = (value: unknown): LadderRung[] => {
  const rungs = records(value);
  return RUNG_LABELS.flatMap(({ key, label }) => {
    const found = rungs.find((rung) => rung['rung'] === key);
    if (!found) return [];
    const status = oneOf(found['status'], RUNG_STATUSES);
    return status ? [{ key, label, status, note: str(found['note']) }] : [];
  });
};

const readDelivery = (value: unknown): DeliveryBlock | null => {
  const block = rec(value);
  const status = block ? str(block['status']) : null;
  return block && status ? { status, note: str(block['note']) } : null;
};

const readEvidence = (value: unknown): EvidenceItem[] =>
  records(value)
    .map((item) => ({ ref: str(item['span_id']) ?? str(item['source']), observation: str(item['observation']) }))
    .filter((item): item is EvidenceItem => item.observation !== null);

const readDecisions = (value: unknown): DecisionItem[] =>
  records(value)
    .map((item) => ({ decision: str(item['decision']), reasoning: str(item['reasoning']) }))
    .filter((item): item is DecisionItem => item.decision !== null);

const readDefects = (value: unknown): AdditionalDefect[] =>
  records(value).map((defect) => ({
    rung: str(defect['rung']),
    fixKey: str(defect['fix_key']),
    summary: str(defect['summary']),
    expected: str(defect['expected']),
    actual: str(defect['actual']),
    fixes: readFixes(defect['suggested_fixes']),
    confidence: num(defect['confidence']),
  }));

/** Reads a stored envelope into the report's shape; null when it carries no verdict at all. */
export function readVerdictReport(investigation: unknown): VerdictReport | null {
  const envelope = rec(investigation);
  const verdict = investigationVerdict(investigation);
  if (!envelope || !verdict) return null;
  const fixKey = str(verdict['fix_key']);

  return {
    disposition: str(verdict['disposition']),
    severity: str(verdict['severity']),
    // "none" is the investigator's answer for a defect it could not classify, not a key.
    fixKey: fixKey === 'none' ? null : fixKey,
    rootCause: str(verdict['root_cause']),
    expected: str(verdict['expected']),
    actual: str(verdict['actual']),
    confidence: num(verdict['confidence']),
    ladder: readLadder(verdict['ladder']),
    firstBrokenRung: str(verdict['first_broken_rung']),
    delivery: readDelivery(verdict['delivery']),
    causeChain: strings(verdict['cause_chain']),
    evidence: readEvidence(verdict['evidence']),
    decisions: readDecisions(verdict['decisions_log']),
    fixes: readFixes(verdict['suggested_fixes']),
    additionalDefects: readDefects(verdict['additional_defects']),
    // The record's economics ride on the envelope camelCased.
    meta: {
      model: str(envelope['model']),
      iterations: num(envelope['iterations']),
      wallSeconds: num(envelope['wallS']),
      costUsd: num(envelope['estCostUsd']),
      traceUrl: str(envelope['investigatorTraceUrl']),
    },
  };
}
